import { Controller } from "@hotwired/stimulus"
import { sendFailureNotification, sendSuccessNotification } from "shared/toast_notifications"

/**
 * A Stimulus controller that manages the behavior of forms with AJAX interactions.
 * It provides functionality for handling successful and failed AJAX responses,
 * managing modal visibility, and resetting the form fields.
 * Typically used for forms that update deployment statuses.
 *
 * Ideally, we issue the async call from here so the page doesn't reload (like many other
 * forms on the app). An improvement for another day.
 */
export default class extends Controller {

    static targets = [
        "form"
    ]

    connect() {
        // Listen for AJAX events on the form
        this.formTarget.addEventListener('ajax:success', this.handleSuccess.bind(this))
        this.formTarget.addEventListener('ajax:error', this.handleError.bind(this))
    }

    disconnect() {
        // Clean up event listeners
        this.formTarget.removeEventListener('ajax:success', this.handleSuccess.bind(this))
        this.formTarget.removeEventListener('ajax:error', this.handleError.bind(this))
    }

    handleSuccess(event) {
        const [data, status, xhr] = event.detail

        // Close the modal
        this.closeModal()

        // Clear the form
        this.clearForm()

        // Show success toast notification
        const deploymentStatusName = data.name || 'Deployment status'
        sendSuccessNotification(`${ deploymentStatusName } saved successfully`)

        // Reload the page to refresh the deployment status list
        // This matches the pattern used by other forms on this page
        window.location.reload()
    }

    handleError(event) {
        const [data, status, xhr] = event.detail

        // Show error toast notification
        let errorMessage = 'Failed to save deployment status'

        if (data && data.error) {
            errorMessage = data.error
        } else if (xhr && xhr.responseJSON && xhr.responseJSON.error) {
            errorMessage = xhr.responseJSON.error
        }

        sendFailureNotification(errorMessage)
    }

    closeModal() {
        // Find the modal element (works for both add and edit modals)
        const modalElement = this.element.closest('.modal')

        if (modalElement) {
            // Use Bootstrap 5 modal API to close
            const modal = bootstrap.Modal.getInstance(modalElement)
            if (modal) {
                modal.hide()
            }
        }
    }

    clearForm() {
        // Reset the form fields
        this.formTarget.reset()
    }
}
